// ─────────────────────────────────────────────────────────────────────────────
// A stored file as the file viewer wants it.
//
// The bytes come through the gateway with the bearer token, which keeps a
// private bucket private and gives the viewer real progress. The signed URL is
// what Copy link and Open externally hand out, because a gateway path with a
// token on the request is not something anyone can paste.
// ─────────────────────────────────────────────────────────────────────────────

import { getConfig, media, readAccessToken } from '@repletoryreach/sdk-api';
import type { RemoteFileSource } from '@repletoryreach/sdk-ui/file-viewer';
import { normaliseValue, type MediaValue } from './types.js';

/**
 * Build a viewer source for a stored file, or for a bare URL.
 *
 * With an `id` the viewer fetches from `GET /v1/media/{id}` with the session's
 * token and links to the signed URL. Without one (a foreign URL pasted into a
 * form) the URL itself is fetched and linked.
 */
export function mediaViewerSource(value: MediaValue | string, opts: { title?: string; details?: RemoteFileSource['details'] } = {}): RemoteFileSource {
  const v = normaliseValue(value);
  if (!v) throw new Error('mediaViewerSource: no value');
  const name = v.original_name ?? undefined;
  if (!v.id) {
    return { kind: 'remote', url: v.url, name, mime: v.mime_type, size: v.size_bytes || undefined, title: opts.title, details: opts.details, id: v.url };
  }
  const cfg = getConfig();
  const token = readAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  return {
    kind: 'remote',
    id: v.id,
    url: `${cfg.apiBaseUrl}${media.downloadUrl(v.id)}`,
    name,
    mime: v.mime_type,
    size: v.size_bytes || undefined,
    headers,
    credentials: cfg.http.credentials,
    linkUrl: v.url || null,
    title: opts.title,
    details: opts.details,
    // The media service extracts text from PDFs, HTML and text files; for the
    // Office families it answers 400, which the viewer reports as "could not be
    // loaded" rather than pretending.
    textFallback: () => media.getText(v.id).then((r) => r.data.text ?? '')
  };
}
